import { create } from "zustand";
import type { FormFlowRuntimeState } from "./types";

const createRuntimeState = (
  initialState: Record<string, unknown>,
): FormFlowRuntimeState => ({
  currentStepIndex: 0,
  formState: initialState,
  completedStepIds: [],
  dirtyStepIds: [],
  submitStatus: "idle",
  errorMessage: undefined,
  isDirty: false,
});

interface FormFlowStore {
  flows: Record<string, FormFlowRuntimeState>;
  initFlow: (instanceId: string, initialState: Record<string, unknown>) => void;
  destroyFlow: (instanceId: string) => void;
  goToStep: (instanceId: string, stepIndex: number) => void;
  setStepData: (
    instanceId: string,
    stateKey: string,
    data: unknown,
    stepId?: string,
  ) => void;
  setFormValue: (instanceId: string, key: string, value: unknown) => void;
  markStepCompleted: (instanceId: string, stepId: string) => void;
  submitStart: (instanceId: string) => void;
  submitSuccess: (instanceId: string) => void;
  submitError: (instanceId: string, message: string) => void;
}

const getFlowOrInitial = (
  flows: Record<string, FormFlowRuntimeState>,
  instanceId: string,
) => flows[instanceId] ?? createRuntimeState({});

export const useFormFlowStore = create<FormFlowStore>((set) => ({
  flows: {},

  initFlow: (instanceId, initialState) =>
    set((state) => {
      if (state.flows[instanceId]) {
        return state;
      }

      return {
        flows: {
          ...state.flows,
          [instanceId]: createRuntimeState(initialState),
        },
      };
    }),

  destroyFlow: (instanceId) =>
    set((state) => {
      const nextFlows = { ...state.flows };
      delete nextFlows[instanceId];

      return { flows: nextFlows };
    }),

  goToStep: (instanceId, stepIndex) =>
    set((state) => {
      const flow = state.flows[instanceId];

      if (!flow) {
        return state;
      }

      return {
        flows: {
          ...state.flows,
          [instanceId]: {
            ...flow,
            currentStepIndex: stepIndex,
          },
        },
      };
    }),

  setStepData: (instanceId, stateKey, data, stepId) =>
    set((state) => {
      const flow = getFlowOrInitial(state.flows, instanceId);
      const dirtyStepIds = flow.dirtyStepIds ?? [];
      const nextDirtyStepIds =
        stepId && !dirtyStepIds.includes(stepId)
          ? [...dirtyStepIds, stepId]
          : dirtyStepIds;

      return {
        flows: {
          ...state.flows,
          [instanceId]: {
            ...flow,
            formState: {
              ...flow.formState,
              [stateKey]: data,
            },
            dirtyStepIds: nextDirtyStepIds,
            isDirty: true,
          },
        },
      };
    }),

  setFormValue: (instanceId, key, value) =>
    set((state) => {
      const flow = getFlowOrInitial(state.flows, instanceId);

      return {
        flows: {
          ...state.flows,
          [instanceId]: {
            ...flow,
            formState: {
              ...flow.formState,
              [key]: value,
            },
            isDirty: true,
          },
        },
      };
    }),

  markStepCompleted: (instanceId, stepId) =>
    set((state) => {
      const flow = state.flows[instanceId];
      const dirtyStepIds = flow?.dirtyStepIds ?? [];

      if (
        !flow ||
        (flow.completedStepIds.includes(stepId) &&
          !dirtyStepIds.includes(stepId))
      ) {
        return state;
      }

      return {
        flows: {
          ...state.flows,
          [instanceId]: {
            ...flow,
            completedStepIds: flow.completedStepIds.includes(stepId)
              ? flow.completedStepIds
              : [...flow.completedStepIds, stepId],
            dirtyStepIds: dirtyStepIds.filter((id) => id !== stepId),
          },
        },
      };
    }),

  submitStart: (instanceId) =>
    set((state) => {
      const flow = state.flows[instanceId];

      if (!flow) {
        return state;
      }

      return {
        flows: {
          ...state.flows,
          [instanceId]: {
            ...flow,
            submitStatus: "submitting",
            errorMessage: undefined,
          },
        },
      };
    }),

  submitSuccess: (instanceId) =>
    set((state) => {
      const flow = state.flows[instanceId];

      if (!flow) {
        return state;
      }

      return {
        flows: {
          ...state.flows,
          [instanceId]: {
            ...flow,
            submitStatus: "success",
            errorMessage: undefined,
            dirtyStepIds: [],
            isDirty: false,
          },
        },
      };
    }),

  submitError: (instanceId, message) =>
    set((state) => {
      const flow = state.flows[instanceId];

      if (!flow) {
        return state;
      }

      return {
        flows: {
          ...state.flows,
          [instanceId]: {
            ...flow,
            submitStatus: "error",
            errorMessage: message,
          },
        },
      };
    }),
}));
